import { isValid, parseISO } from 'date-fns';
import { formatAmountWithDecimals } from '~/utils/formatNumber';

export type F24AmountInput = number | string | null | undefined;

export const toAmountNumber = (value: F24AmountInput): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Split an amount into its integer and decimal parts so each can be rendered on its own side of the
 * comma that is already pre-printed on the F24 template (otherwise the rendered comma doubles it).
 * Returns null when there is no amount to show.
 */
export const splitF24Amount = (value: F24AmountInput): { integerPart: string; decimals: string } | null => {
  const parsed = toAmountNumber(value);
  if (parsed === null) return null;
  // formatAmountWithDecimals always emits exactly two decimals separated by a comma (it_IT).
  const [integerPart = '', decimals = ''] = formatAmountWithDecimals(parsed).split(',');
  return { integerPart, decimals };
};

/**
 * The F24 form already prints the "IT" country code before the IBAN boxes, so strip it (and any
 * spaces) from the value we render — only the rest of the IBAN should fill the boxes.
 */
export const stripIbanCountryCode = (iban?: string | null): string | null => {
  const compact = iban?.replace(/\s/g, '');
  if (!compact) return null;
  return /^IT/i.test(compact) ? compact.slice(2) : compact;
};

/**
 * Split a "MMAAAA" reference period (e.g. "012024") into its month and year, which sit on either
 * side of the divider printed on the INPS / altri-enti periodo columns. Null when there's nothing.
 */
export const splitPeriodo = (value?: string | null): { mese: string; anno: string } | null => {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  return { mese: trimmed.slice(0, 2), anno: trimmed.slice(2) };
};

export const splitDateParts = (iso?: string | null): { gg: string; mm: string; aaaa: string } => {
  if (!iso) return { gg: '', mm: '', aaaa: '' };
  const date = parseISO(iso);
  if (!isValid(date)) return { gg: '', mm: '', aaaa: '' };
  return {
    gg: String(date.getDate()).padStart(2, '0'),
    mm: String(date.getMonth() + 1).padStart(2, '0'),
    aaaa: String(date.getFullYear()),
  };
};
